import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Button from "@/components/Button/Button";
import Loader from "@/components/Loader/Loader";
import { BackArrowIcon } from "@/components/SVGComponents/MemberIcons";
import { openRazorpayCheckout } from "@/lib/razorPay/razorpayCheckout";
import toast from "@/lib/toast";
import { combineClasses, saveMySubscription } from "@/lib/utils";
import {
  formatPayAmount,
  formatPlanDurationShort,
  formatPrice,
  hasQueuedSubscriptionData,
} from "@/pages/Profile/Subscription/subscriptionFormatters";
import { PROFILE_BASE_PATH, PROFILE_CHILD_PATHS } from "@/pages/Profile/profilePaths";
import RazorpayService from "@/services/api/razorpay";
import SubscriptionService from "@/services/api/subscription";
import routes from "@/routes/routes";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchSubscriptionPlans,
  fetchUserSubscription,
} from "@/store/slices/subscriptionSlice";
import styles from "./ChangeMembership.module.scss";

export type UpgradeOption = "now" | "next_month";

const UPGRADE_OPTIONS: Array<{
  value: UpgradeOption;
  title: string;
  description: string;
}> = [
  {
    value: "now",
    title: "Pay Now",
    description: "Activate Immediately.",
  },
  {
    value: "next_month",
    title: "Pay Next Month",
    description:
      "Continue with your current plan for now. Your new membership will start and be charged on your next billing date.",
  },
];

const subscriptionPath = `${PROFILE_BASE_PATH}/${PROFILE_CHILD_PATHS.SUBSCRIPTION}`;

const ChangeMembership = () => {
  const { planId } = useParams<{ planId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentSubscription, plans, isLoadingPlans } = useAppSelector(
    (state) => state.subscription,
  );
  const [upgradeOption, setUpgradeOption] = useState<UpgradeOption>("next_month");
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const selectedPlanId = Number(planId);
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId) ?? null;
  const currentPlanAmount = currentSubscription?.amount ?? 0;
  const selectedPlanAmount = selectedPlan?.amount ?? 0;
  const isUpgrade = selectedPlanAmount > currentPlanAmount;
  const hasQueuedPlan = hasQueuedSubscriptionData(currentSubscription);
  const canPayNow = isUpgrade && !hasQueuedPlan;

  const visibleUpgradeOptions = useMemo(
    () => UPGRADE_OPTIONS.filter((option) => option.value === "next_month" || canPayNow),
    [canPayNow],
  );

  useEffect(() => {
    if (!canPayNow && upgradeOption === "now") {
      setUpgradeOption("next_month");
    }
  }, [canPayNow, upgradeOption]);

  useEffect(() => {
    dispatch(fetchSubscriptionPlans());
    dispatch(fetchUserSubscription());
  }, [dispatch]);

  useEffect(() => {
    if (!currentSubscription) {
      navigate(routes.privateRoutes.MEMBERSHIP.path, { replace: true });
    }
  }, [currentSubscription, navigate]);

  useEffect(() => {
    if (!planId || Number.isNaN(selectedPlanId)) {
      navigate(routes.privateRoutes.MEMBERSHIP.path, { replace: true });
    }
  }, [navigate, planId, selectedPlanId]);

  useEffect(() => {
    if (plans.length > 0 && !selectedPlan) {
      navigate(routes.privateRoutes.MEMBERSHIP.path, { replace: true });
    }
  }, [navigate, plans.length, selectedPlan]);

  const handlePayNextMonth = async () => {
    if (!selectedPlan || isPaying) {
      return;
    }

    try {
      setIsPaying(true);
      setPaymentError(null);

      await toast.promise(
        RazorpayService.changePlan({
          planId: selectedPlan.id,
          immediate: false,
        }).then(async (response) => {
          const subscriptionResponse = await SubscriptionService.getUserSubscription();

          if (subscriptionResponse?.data) {
            saveMySubscription(subscriptionResponse.data);
          }

          await dispatch(fetchUserSubscription()).unwrap();
          return response;
        }),
        {
          loading: "Scheduling plan change...",
          success: (response) => response?.message || "Plan change scheduled successfully",
          error: (error) =>
            error?.message || error?.error || "Failed to schedule plan change. Please try again.",
        },
      );

      navigate(subscriptionPath);
    } catch {
      // Toast handles the error message.
    } finally {
      setIsPaying(false);
    }
  };

  const handlePayNow = async () => {
    if (!selectedPlan || isPaying) {
      return;
    }

    try {
      setIsPaying(true);
      setPaymentError(null);

      const response = await RazorpayService.orderInitiate({
        planId: selectedPlan.id,
        paymentProvider: "RAZORPAY_SUBSCRIPTION",
      });

      const { paymentOrderId, paymentKeyId } = response?.data || {};

      if (!paymentOrderId || !paymentKeyId) {
        throw new Error("Unable to start payment");
      }

      await openRazorpayCheckout({
        key: paymentKeyId,
        subscription_id: paymentOrderId,
        onSuccess: async (razorpayResponse) => {
          try {
            await RazorpayService.subscriptionCallback(razorpayResponse);

            const subscriptionResponse = await SubscriptionService.getUserSubscription();

            if (subscriptionResponse?.data) {
              saveMySubscription(subscriptionResponse.data);
            }

            navigate(routes.privateRoutes.PAYMENT_SUCCESSFUL.path);
          } catch (callbackError) {
            console.error(callbackError);
            setPaymentError("Payment verification failed. Please contact support.");
          }
        },
        onFailure: () => {
          setPaymentError("Payment failed. Please try again.");
        },
      });
    } catch (err) {
      console.error(err);
      setPaymentError("Unable to start payment. Please try again.");
    } finally {
      setIsPaying(false);
    }
  };

  const handlePay = () => {
    if (upgradeOption === "next_month") {
      void handlePayNextMonth();
      return;
    }

    if (!canPayNow) {
      return;
    }

    void handlePayNow();
  };

  const getPayButtonLabel = () => {
    if (isPaying) {
      return "Processing...";
    }

    if (upgradeOption === "next_month" || !canPayNow) {
      return "Confirm Change";
    }

    return `Pay ₹${formatPayAmount(selectedPlan?.amount)}`;
  };

  if ((isLoadingPlans && plans.length === 0) || !selectedPlan || !currentSubscription) {
    return <Loader />;
  }

  return (
    <section className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.mobileHeader}>
          <Link
            aria-label="Back to membership plans"
            className={styles.backButton}
            to={routes.privateRoutes.MEMBERSHIP.path}
          >
            <BackArrowIcon />
          </Link>
          <h1>Membership</h1>
          <span className={styles.headerSpacer} />
        </div>

        <div className={styles.content}>
          <header className={styles.intro}>
            <h2>Change Membership</h2>
            <p>Get more with an upgraded membership.</p>
          </header>

          <article className={styles.summaryCard}>
            <div className={styles.summaryRow}>
              <span>Current Plan</span>
              <strong>
                ₹{formatPrice(currentSubscription?.amount)} /{" "}
                {formatPlanDurationShort(currentSubscription?.durationMonth)}
              </strong>
            </div>
            <div className={styles.summaryDivider} />
            <div className={styles.summaryRow}>
              <span>Selected Plan</span>
              <strong className={styles.selectedPlanValue}>
                ₹{formatPayAmount(selectedPlan?.amount)} /{" "}
                {formatPlanDurationShort(selectedPlan?.planDurationMonth)}
              </strong>
            </div>
          </article>

          <section className={styles.upgradeSection}>
            <h3>When would you like to upgrade?</h3>

            <div className={styles.options}>
              {visibleUpgradeOptions.map((option) => {
                const isSelected = upgradeOption === option.value;

                return (
                  <button
                    className={combineClasses(
                      styles.optionCard,
                      isSelected ? styles.optionCardSelected : "",
                    )}
                    key={option.value}
                    onClick={() => setUpgradeOption(option.value)}
                    type="button"
                  >
                    <span
                      className={`${styles.radio} ${isSelected ? styles.radioSelected : ""}`}
                    />
                    <span className={styles.optionCopy}>
                      <strong>{option.title}</strong>
                      <span>{option.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {paymentError ? <p className={styles.error}>{paymentError}</p> : null}
        </div>

        <Button
          className={styles.payButton}
          disabled={isPaying}
          onClick={handlePay}
          type="button"
        >
          {getPayButtonLabel()}
        </Button>
      </div>
    </section>
  );
};

export default ChangeMembership;
